"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Creating a Recipe isn't limited by your plan",
  },
  {
    topic: "Unique name",
    rule: "Recipe names must be unique per company, checked case-insensitively",
  },
  {
    topic: "Remark is required",
    rule: "Unlike most optional notes fields elsewhere, a Recipe won't save without something in Remark",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a Recipe, only Edit and Delete",
  },
  {
    topic: "Reusable",
    rule: "Nothing stops multiple items from pointing at the same Recipe",
  },
  {
    topic: "Editing doesn't touch history",
    rule: "Changing a Recipe's Process list only affects future production, not orders already created with the old sequence",
  },
  {
    topic: "Deleting",
    rule: "Blocked if any item still names this Recipe as its Process Route, or if any Production Order was created from it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A Recipe is an ordered list of Processes, not a bill of materials" },
      { heading: "2. Where raw materials actually get entered" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function RecipePage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Recipe
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone defining the sequence of Processes an item is produced through
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">Recipe</span> is
            simpler than the name suggests. It holds a name and an ordered list
            of Processes, nothing about materials or output. If you&apos;re
            picturing a bill of materials, that&apos;s a different concept
            entirely, and it doesn&apos;t live here.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Ordered list of Processes */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A Recipe is an ordered list of Processes, not a bill of materials
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Add Recipe form makes this obvious once you look at it directly.
          </p>
          <Definitions
            items={[
              {
                term: "Recipe Name",
                desc: "Must be unique across your company.",
              },
              {
                term: "Processes",
                desc: "A list of steps you pick and put in order. The order you choose is the production sequence.",
              },
              {
                term: "Remark",
                desc: "A required note, unlike most optional remarks elsewhere in the app.",
              },
              {
                term: "No finished item field",
                desc: "The Recipe doesn't name what it produces. The link runs the other way around.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Recipe form, showing name, ordered Processes, and Remark, nothing else." />

          {/* 2. Where materials come from */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Where raw materials actually get entered
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            An item connects to a Recipe from the item&apos;s own side, and
            materials are handled completely separately from the Recipe itself.
          </p>
          <Definitions
            items={[
              {
                term: "The link direction",
                desc: "An item's Production Data has a Process Route field that points at a Recipe, not the other way around.",
              },
              {
                term: "One Recipe, many items",
                desc: "Nothing prevents several different items from all pointing at the same Recipe.",
              },
              {
                term: "Raw materials during a Process Route run",
                desc: "Entered fresh on the Production Order itself, step by step, as each process actually happens.",
              },
              {
                term: "The BOM-based method",
                desc: "This is the other production method, where the materials list is defined in advance directly on the item. It doesn't involve a Recipe at all.",
              },
            ]}
          />
          <Callout type="warning">
            If you&apos;re looking for where to define what raw materials go
            into a finished item, it&apos;s not here. Check the item&apos;s
            own Production Data tab instead.
          </Callout>
          <Screenshot caption="Screenshot: the Process Route field on an item's Production Data tab, pointing at a Recipe." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/process"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Process <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              The individual steps a Recipe strings together
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where an item actually points at this Recipe
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/quality-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Quality Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              QC standards that can be linked to production output
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
